import EditOutlined from '@ant-design/icons/EditOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { useCreateReport } from '@vindr/services/actions/report.action';
import { USER_STATUS } from '@vindr/services/constants/filter.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { REPORT_STATUS } from '@vindr/services/constants/report.constant';
import keycloak from '@vindr/services/keycloak';
import { uuidv4 } from '@vindr/utils/helpers/common.helper';
import { Empty as AntEmpty, Button, Form } from 'antd';
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import isEmpty from 'lodash/isEmpty';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import StructuredReportForm from '@vindr/ui/src/components/common/StructuredReport/StructuredReportForm';
import DropdownButton from '../DropdownButton';
import { ReportFormStates } from '../ReportForm';
import SelectOrderHeader from './SelectOrderHeader';
import { Empty, TextEditor } from '@vindr/ui';
import { MESSAGE } from '@vindr/services/constants/message.constant';

const UUID_editor = uuidv4();

export const ReportEditor = memo(
  ({
    assign,
    canSeeReport,
    className,
    esignStatus,
    isCompare,
    is_structure_report,
    matchedOrders,
    message,
    onReloadingReport,
    report,
    selectedOrder,
    selectedReport,
    setEditOrder,
    setNewReport,
    setOpenSelectTemplate,
    setSelectedOrder,
    structureSections,
    structuredReportRef,
    webcamRef,
  }: {
    assign: ReportFormStates['assign'];
    canSeeReport: ReportFormStates['canSeeReport'];
    className?: string;
    esignStatus: ReportFormStates['esignStatus'];
    isCompare: boolean;
    is_structure_report: ReportFormStates['is_structure_report'];
    matchedOrders: ReportFormStates['matchedOrders'];
    message: ReportFormStates['message'];
    onReloadingReport: ReportFormStates['onReloadingReport'];
    report: ReportFormStates['report'];
    selectedOrder: ReportFormStates['selectedOrder'];
    selectedReport: ReportFormStates['selectedReport'];
    setEditOrder: ReportFormStates['setEditOrder'];
    setNewReport: ReportFormStates['setNewReport'];
    setOpenSelectTemplate: ReportFormStates['setOpenSelectTemplate'];
    setSelectedOrder: ReportFormStates['setSelectedOrder'];
    structureSections: ReportFormStates['structureSections'];
    structuredReportRef: ReportFormStates['structuredReportRef'];
    webcamRef: ReportFormStates['webcamRef'];
  }) => {
    const { t } = useTranslation('Vindoc');
    const { mutate: createReport, isLoading: isCreatingReport } = useCreateReport();
    const sessionUser = useGetAccounts(
      { offset: 0, limit: 100, statuses: [USER_STATUS.ACTIVE], ids: [report?.session] },
      { enabled: !!report?.session }
    )?.data?.data?.[0];

    const handleCreateReport = useCallback(
      (isStructureReport = false) => {
        if (!selectedOrder) {
          message.error(t('No selected order'));

          return;
        }
        createReport(
          {
            institution_id: selectedOrder?.institution_id,
            order_id: selectedOrder?.id,
            order_assign_id: assign?.id,
            is_structure_report: !!isStructureReport,
          },
          {
            onSuccess: (res) => {
              setNewReport({ is_structure_report: isStructureReport });
            },
            onError: (error) => {
              message.error({
                content: t(
                  MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001
                ),
              });
            },
          }
        );
      },
      [assign?.id, createReport, message, selectedOrder, t]
    );

    const createReportMenuItems: MenuItemType[] = useMemo(
      () =>
        [
          keycloak.hasOneOfPerm([PERMISSIONS.report.create]) && {
            key: '1',
            label: t('Basic Report'),
            disabled:
              isCreatingReport ||
              onReloadingReport ||
              !keycloak.hasOneOfPerm([PERMISSIONS.report.create]),
            onClick: () => handleCreateReport(),
          },
          keycloak.hasOneOfPerm([PERMISSIONS.report.structure]) && {
            key: '2',
            label: t('Structured Report'),
            disabled:
              isCreatingReport ||
              onReloadingReport ||
              !keycloak.hasOneOfPerm([PERMISSIONS.report.structure]),
            onClick: () => handleCreateReport(true),
          },
        ].filter(Boolean),
      [handleCreateReport, onReloadingReport, t, isCreatingReport]
    );

    return (
      <section className={className}>
        <SelectOrderHeader
          disabled={isCompare || selectedReport?.status === REPORT_STATUS.READING}
          disabledAction={isCompare || selectedReport?.status === REPORT_STATUS.READING}
          esignStatus={esignStatus}
          isCompare={isCompare}
          matchedOrders={matchedOrders}
          selectedOrder={selectedOrder}
          action={
            <Button size="small" type="text">
              <EditOutlined
                className="cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditOrder({ editingOrder: selectedOrder, open: true });
                }}
              />
            </Button>
          }
          onSelect={setSelectedOrder}
          onEditOrder={(order) => {
            setEditOrder({ editingOrder: order, open: true });
          }}
        />
        {!selectedReport ? (
          <div className="flex min-h-80 flex-col items-center justify-center rounded-sm border border-secondary-main">
            <Empty
              className="m-0 text-lg"
              description={t('No report available')}
              image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
            />
            {keycloak.hasOneOfPerm([PERMISSIONS.report.create]) && (
              <DropdownButton
                className="mt-1 flex items-center"
                menu={{ items: createReportMenuItems }}
                placement="bottomLeft"
              >
                <Button className="mt-1 flex items-center" size="small" type="primary">
                  <PlusOutlined />
                  {t('New Report')}
                </Button>
              </DropdownButton>
            )}
          </div>
        ) : canSeeReport(selectedReport) ? (
          <>
            <div>
              {is_structure_report ? (
                <StructuredReportForm
                  ref={structuredReportRef}
                  className="m-0"
                  disabled={selectedReport?.status !== REPORT_STATUS.READING}
                  sections={structureSections}
                  onChooseTemplate={() => setOpenSelectTemplate(true)}
                />
              ) : (
                <Form.Item
                  className="mb-0"
                  name="contentAndConclusion"
                  style={{ height: 'calc(100dvh - 355px)' }}
                >
                  <TextEditor
                    // className="h-full"
                    height={'calc(100dvh - 355px)'}
                    id={UUID_editor} //? For multiple text editor in one site
                    menubar={false}
                    disabled={
                      is_structure_report || selectedReport?.status !== REPORT_STATUS.READING
                    }
                    onKeyDown={(e) => webcamRef.current?.handleKeyDown(e)}
                  />
                </Form.Item>
              )}
            </div>
            <Form.Item hidden name="structure">
              <i />
            </Form.Item>
            <Form.Item hidden name="is_structure_report">
              <i />
            </Form.Item>
          </>
        ) : (
          <div className="flex min-h-80 flex-col items-center justify-center rounded-sm border border-secondary-main">
            <Empty
              image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
              description={
                isEmpty(sessionUser)
                  ? t('This report is being modified by another user')
                  : `${t('This report is being modified by')} ${sessionUser?.username}${
                      sessionUser?.fullname ? ` : ${sessionUser?.fullname}` : ''
                    }`
              }
            />
          </div>
        )}
      </section>
    );
  }
);
ReportEditor.displayName = 'ReportEditor';
